"use client"

import type { ElementType } from "react"
import { SidebarProvider } from "./SidebarContext"
import type { SidebarProps, SidebarRenderProp } from "./Sidebar.types"
import { DEFAULT_CONFIG } from "./Sidebar.constants"

/**
 * Componente raíz del Sidebar con arquitectura headless
 *
 * @description
 * Componente principal que provee el contexto compartido a todos los
 * componentes primitivos del sidebar (SidebarNav, SidebarToggle, etc.).
 *
 * Este componente NO renderiza ningún elemento visual por sí mismo,
 * solo provee el Provider de contexto. Los estilos y estructura visual
 * se definen componiendo los primitivos hijos.
 *
 * Soporta dos modos:
 * - **Uncontrolled**: Usa `defaultOpen` para estado interno
 * - **Controlled**: Usa `open` + `onOpenChange` para control externo
 *
 * También soporta render props para acceder al estado del sidebar:
 *
 * @example
 * ```tsx
 * // Uso básico (children estáticos)
 * <Sidebar defaultOpen={true} collapseMode="collapse">
 *   <SidebarNav>
 *     <SidebarToggle />
 *     <SidebarContent>
 *       <SidebarItem href="/" icon={<HomeIcon />} />
 *     </SidebarContent>
 *   </SidebarNav>
 * </Sidebar>
 * ```
 *
 * @example
 * ```tsx
 * // Con render props (acceso al estado)
 * <Sidebar>
 *   {({ open, collapsed }) => (
 *     <>
 *       <SidebarNav className={cn(!open && 'hidden')}>
 *         <SidebarContent>
 *           <SidebarItem label={open ? "Home" : undefined} />
 *         </SidebarContent>
 *       </SidebarNav>
 *       <div>Sidebar is {open ? 'open' : 'closed'}</div>
 *     </>
 *   )}
 * </Sidebar>
 * ```
 *
 * @example
 * ```tsx
 * // Modo controlled
 * function App() {
 *   const [open, setOpen] = useState(true)
 *
 *   return (
 *     <Sidebar open={open} onOpenChange={setOpen}>
 *       <SidebarNav>...</SidebarNav>
 *     </Sidebar>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Con configuración custom
 * <Sidebar
 *   collapseMode="hide"
 *   dimensions={{ expandedWidth: "20rem" }}
 *   animations={{ widthTransitionDuration: 500 }}
 * >
 *   <SidebarNav>...</SidebarNav>
 * </Sidebar>
 * ```
 *
 * @note
 * Los colores se obtienen automáticamente de los tokens CSS de shadcn definidos
 * en globals.css (--sidebar, --sidebar-accent, --sidebar-primary, etc.)
 */
export function Sidebar({
  children,
  className,
  style,
  defaultOpen = DEFAULT_CONFIG.defaultOpen,
  open,
  onOpenChange,
  collapseMode = DEFAULT_CONFIG.collapseMode,
  hideBehaviour = DEFAULT_CONFIG.hideBehaviour,
  hideOpensBehavior = DEFAULT_CONFIG.hideOpensBehavior,
  layoutBehaviour = DEFAULT_CONFIG.layoutBehaviour,
  dimensions,
  animations,
  toggleId = DEFAULT_CONFIG.toggleId,
  debug = DEFAULT_CONFIG.debug,
  enableFluidIndicator = false,
  enableTooltip = true,
  liquidGlass,
  as,
}: SidebarProps) {
  // Determinar si children es render prop o ReactNode
  const isRenderProp = typeof children === "function"

  // Wrapper component (default: div, customizable con 'as')
  const Component = (as || "div") as ElementType

  return (
    <SidebarProvider
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      collapseMode={collapseMode}
      hideBehaviour={hideBehaviour}
      hideOpensBehavior={hideOpensBehavior}
      layoutBehaviour={layoutBehaviour}
      dimensions={dimensions}
      animations={animations}
      toggleId={toggleId}
      debug={debug}
      enableFluidIndicator={enableFluidIndicator}
      enableTooltip={enableTooltip}
      liquidGlass={liquidGlass}
    >
      <Component className={className} style={style}>
        {isRenderProp
          ? (children as SidebarRenderProp)({
              open: open ?? defaultOpen,
              collapsed: !open && collapseMode === "collapse",
              collapseMode,
              layoutBehaviour,
            })
          : children}
      </Component>
    </SidebarProvider>
  )
}
